import { useEffect, useRef, useState } from "react";
import { Box, Eye, Pause, Play, RotateCcw } from "lucide-react";
import Konteyner3D from "./Konteyner3D";

const fmt = (v) => (Number.isFinite(v) ? v.toLocaleString("tr-TR") : "");

const METIN = {
  tr: {
    views: { "3d": "3D", kapi: "Kapıdan", yan: "Yandan", ust: "Üstten" },
    hint: "Paketin üzerine gelin: ölçü, ağırlık, konum ve yükleme sırası.",
    kamera: "Kamera", duvar: "Duvarlar", cog: "Ağırlık merkezi", tavan: "Tavan boşluğu",
    yukle: "Yükle", bosalt: "Boşalt", dur: "Duraklat", hiz: "Hız", adim: "Yükleme adımı",
    sayac: (a, b) => `Konteynerde ${a} / ${b} paket`, hata: "3D görünüm açılamadı: ", webgl: "WebGL desteklenmiyor",
  },
  en: {
    views: { "3d": "3D", kapi: "Door", yan: "Side", ust: "Top" },
    hint: "Hover a bundle for size, weight, position and loading step.",
    kamera: "Camera", duvar: "Walls", cog: "Centre of gravity", tavan: "Roof clearance",
    yukle: "Load", bosalt: "Unload", dur: "Pause", hiz: "Speed", adim: "Loading step",
    sayac: (a, b) => `${a} / ${b} bundles in container`, hata: "3D view could not start: ", webgl: "WebGL not supported",
  },
};
const VIEW_KEYS = ["3d", "kapi", "yan", "ust"];

const SPEEDS = [
  { v: 1.5, label: "1×" },
  { v: 3, label: "2×" },
  { v: 6, label: "4×" },
  { v: 12, label: "8×" },
];

/**
 * Seçili konteynerin 3D görünümü + yükleme/boşaltma oynatıcısı.
 * result: backend'den gelen alternatif, no: konteyner numarası
 */
export default function Konteyner3DView({ result, no, colorOf, labelOf, title, lang = "tr", hint }) {
  const T = METIN[lang] || METIN.tr;
  const hostRef = useRef(null);
  const tipRef = useRef(null);
  const sceneRef = useRef(null);
  const fnRef = useRef({ colorOf, labelOf });
  const [state, setState] = useState({ t: 0, N: 0, dir: 0, inside: 0 });
  const [view, setView] = useState("3d");
  const [speed, setSpeed] = useState(3);
  const [walls, setWalls] = useState(true);
  const [cog, setCog] = useState(true);
  const [clear, setClear] = useState(true);
  const [error, setError] = useState("");

  fnRef.current = { colorOf, labelOf };

  useEffect(() => {
    let scene;
    try {
      scene = new Konteyner3D(hostRef.current, {
        colorOf: (id) => fnRef.current.colorOf(id),
        labelOf: (id) => fnRef.current.labelOf(id),
        onState: (s) => setState(s),
        tipEl: tipRef.current,
        lang,
      });
      scene.rate = 3;
      sceneRef.current = scene;
    } catch (e) {
      setError(T.hata + (e.message || T.webgl));
    }
    return () => {
      scene?.destroy();
      sceneRef.current = null;
    };
    // dil değişirse bileşen key ile yeniden kurulur
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (sceneRef.current && result) sceneRef.current.show(result, no);
  }, [result, no]);

  const changeView = (v) => {
    setView(v);
    sceneRef.current?.setView(v);
  };

  const s = sceneRef.current;
  return (
    <section className="kyp-panel kyp-viewer">
      <div className="kyp-panel-header">
        <div>
          <h2>
            <Box size={18} /> {title}
          </h2>
          <p>{hint ?? T.hint}</p>
        </div>
        <div className="kyp-seg" role="group" aria-label={T.kamera}>
          {VIEW_KEYS.map((k) => (
            <button key={k} type="button" aria-pressed={view === k} onClick={() => changeView(k)}>
              {T.views[k]}
            </button>
          ))}
        </div>
      </div>
      <div className="kyp-stage" ref={hostRef}>
        {error && <div className="kyp-stage-msg">{error}</div>}
        <div className="kyp-stage-overlay">
          <button type="button" aria-pressed={walls} onClick={() => { setWalls(!walls); s?.setWalls(!walls); }}>
            <Eye size={14} /> {T.duvar}
          </button>
          <button type="button" aria-pressed={cog} onClick={() => { setCog(!cog); s?.setCog(!cog); }}>
            {T.cog}
          </button>
          <button type="button" aria-pressed={clear} onClick={() => { setClear(!clear); s?.setClearance(!clear); }}>
            {T.tavan}
          </button>
        </div>
        <div className="kyp-tip" ref={tipRef} hidden />
      </div>
      <div className="kyp-player">
        <button type="button" className="kyp-primary-btn sm" onClick={() => s?.play(1)} disabled={!state.N}>
          {state.dir > 0 ? <Pause size={15} /> : <Play size={15} />} {state.dir > 0 ? T.dur : T.yukle}
        </button>
        <button type="button" className="kyp-secondary-btn sm" onClick={() => s?.play(-1)} disabled={!state.N}>
          {state.dir < 0 ? <Pause size={15} /> : <RotateCcw size={15} />} {state.dir < 0 ? T.dur : T.bosalt}
        </button>
        <select
          aria-label={T.hiz}
          value={speed}
          onChange={(e) => {
            setSpeed(+e.target.value);
            if (s) s.rate = +e.target.value;
          }}
        >
          {SPEEDS.map((x) => (
            <option key={x.v} value={x.v}>
              {x.label}
            </option>
          ))}
        </select>
        <input
          type="range"
          min="0"
          max={state.N || 1}
          step="0.01"
          value={state.t}
          onChange={(e) => s?.seek(+e.target.value)}
          aria-label={T.adim}
        />
        <span className="kyp-count">
          {state.N ? T.sayac(fmt(state.inside), fmt(state.N)) : ""}
        </span>
      </div>
    </section>
  );
}
